// fork from https://github.com/xmppjs/xmpp.js/blob/main/packages/client/index.js
// changes: pass token to sasl middleware and use oath mechanism, remove tcp and tls as they are depending on node specific modules

import { xml, Client as CoreClient } from '@xmpp/client-core'
import { Client } from '@xmpp/client'

import _reconnect from '@xmpp/reconnect'
import _websocket from '@xmpp/websocket'
import _middleware from '@xmpp/middleware'
import _streamFeatures from '@xmpp/stream-features'
import _iqCaller from '@xmpp/iq/caller'
import _iqCallee from '@xmpp/iq/callee'
import _resolve from '@xmpp/resolve'

// Stream features - order matters and define priority
import _starttls from '@xmpp/starttls/client'
// eslint-disable-next-line import/order
import _sasl from './XMPPSasl'
import _resourceBinding from '@xmpp/resource-binding'
import _sessionEstablishment from '@xmpp/session-establishment'
import _streamManagement from '@xmpp/stream-management'

// SASL mechanisms - order matters and define priority
import { Options } from '@xmpp/client'
import oauthMech from './saslXoauth'

const getDomain = (service: string) => {
  const domain = service.split('://')[1] || service
  return domain.split(':')[0]?.split('/')[0]
}

export const client = (
  options: Omit<Options, 'credentials' | 'username' | 'password'> & {
    credentials: { refreshToken?: string; token: string; username: string }
  }
): Client => {
  const { resource, credentials, ...params } = options

  const { domain, service } = params
  if (!domain && service) {
    params.domain = getDomain(service)
  }

  const entity = new CoreClient(params)

  const reconnect = _reconnect({ entity })
  const websocket = _websocket({ entity })

  const middleware = _middleware({ entity })
  const streamFeatures = _streamFeatures({ middleware })
  const resolve = _resolve({ entity })
  // Stream features - order matters and define priority
  const starttls = _starttls({ streamFeatures })
  const sasl = _sasl({ streamFeatures }, credentials)
  const streamManagement = _streamManagement({
    streamFeatures,
    entity,
    middleware,
  })
  const iqCaller = _iqCaller({ middleware, entity })
  const iqCallee = _iqCallee({ middleware, entity })
  const resourceBinding = _resourceBinding({ iqCaller, streamFeatures }, resource)
  const sessionEstablishment = _sessionEstablishment({
    iqCaller,
    streamFeatures,
  })

  const oauth = (saslInstance: typeof sasl) => {
    saslInstance.use(oauthMech)
  }

  // SASL mechanisms - order matters and define priority
  const mechanisms = Object.entries({
    oauth,
  }).map(([k, v]) => ({ [k]: v(sasl) })) as any

  return Object.assign(entity, {
    entity,
    reconnect,
    websocket,
    middleware,
    streamFeatures,
    iqCaller,
    iqCallee,
    resolve,
    starttls,
    sasl,
    resourceBinding,
    sessionEstablishment,
    streamManagement,
    mechanisms,
  }) as any
}

export { xml }
